import { ABOUT, AREAS, Area, BRAND, HOME_FAQS, IMG, KEYWORDS, Service, SERVICES } from "./site";

export const SITE = (process.env.REACT_APP_SITE_URL || "https://allconceptconcrete.com").replace(/\/$/, "");
const BIZ_ID = `${SITE}/#business`;
const ctx = "https://schema.org";

type Faq = { q: string; a: string };
type Json = Record<string, unknown>;
export type PageSeo = {
  path: string; title: string; description: string; h1: string; intro: string;
  bullets: string[]; faqs: Faq[]; schema: Json[];
};

export const HOME_TITLE = "Polished Concrete & Epoxy Flooring Gold Coast | All Concept Concrete";
export const HOME_DESC = `All Concept Concrete — Gold Coast polished concrete flooring, epoxy flooring and commercial flooring specialists. Industrial floors, grind & seal, resurfacing. Call ${BRAND.phone}.`;
export const HOME_H1 = "Polished Concrete, Epoxy & Commercial Flooring Gold Coast";

export function businessSchema(): Json {
  return {
    "@context": ctx, "@type": ["LocalBusiness", "HomeAndConstructionBusiness"], "@id": BIZ_ID,
    name: BRAND.name, url: SITE, logo: `${SITE}/logo.svg`, image: IMG.hero, description: ABOUT,
    telephone: BRAND.phoneE164, email: BRAND.email, priceRange: "$$",
    address: { "@type": "PostalAddress", streetAddress: BRAND.street, addressLocality: BRAND.suburb, addressRegion: BRAND.region, postalCode: BRAND.postcode, addressCountry: "AU" },
    geo: { "@type": "GeoCoordinates", latitude: BRAND.geo.lat, longitude: BRAND.geo.lng },
    hasMap: BRAND.mapsUrl, sameAs: [BRAND.mapsUrl],
    openingHoursSpecification: [{ "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "06:30", closes: "17:00" }],
    areaServed: [{ "@type": "City", name: "Gold Coast" }, ...AREAS.map((a) => ({ "@type": "Place", name: `${a.name}, ${a.state || "QLD"}` }))],
    knowsAbout: KEYWORDS,
    hasOfferCatalog: {
      "@type": "OfferCatalog", name: "Concrete flooring services",
      itemListElement: SERVICES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.title, url: `${SITE}/services/${s.slug}` } })),
    },
  };
}

const websiteSchema = (): Json => ({ "@context": ctx, "@type": "WebSite", "@id": `${SITE}/#website`, url: SITE, name: BRAND.name, publisher: { "@id": BIZ_ID }, inLanguage: "en-AU" });

const faqSchema = (faqs: Faq[]): Json => ({
  "@context": ctx, "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

const crumbs = (items: [string, string][]): Json => ({
  "@context": ctx, "@type": "BreadcrumbList",
  itemListElement: [["Home", "/"], ...items].map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE}${path}` })),
});

const webPage = (path: string, title: string, description: string): Json => ({
  "@context": ctx, "@type": "WebPage", "@id": `${SITE}${path}#webpage`, url: `${SITE}${path}`, name: title, description,
  isPartOf: { "@id": `${SITE}/#website` }, about: { "@id": BIZ_ID }, inLanguage: "en-AU",
});

function servicePage(s: Service): PageSeo {
  const path = `/services/${s.slug}`;
  return {
    path, title: s.metaTitle, description: s.metaDesc, h1: s.h1, intro: s.intro,
    bullets: [...s.applications, ...s.benefits], faqs: s.faqs,
    schema: [
      webPage(path, s.metaTitle, s.metaDesc),
      { "@context": ctx, "@type": "Service", "@id": `${SITE}${path}#service`, name: s.title, serviceType: s.title, description: s.intro, url: `${SITE}${path}`, image: s.image, provider: { "@id": BIZ_ID }, areaServed: [{ "@type": "City", name: "Gold Coast" }, ...AREAS.map((a) => ({ "@type": "Place", name: a.name }))] },
      faqSchema(s.faqs),
      crumbs([["Services", "/#services"], [s.title, path]]),
    ],
  };
}

function areaPage(a: Area): PageSeo {
  const path = `/areas/${a.slug}`;
  const title = `Polished Concrete & Epoxy Flooring ${a.name} | All Concept Concrete`;
  const services = a.focus.map((f) => SERVICES.find((s) => s.slug === f)).filter(Boolean) as Service[];
  return {
    path, title, description: a.metaDesc, h1: a.h1, intro: a.intro,
    bullets: [...a.industries, ...a.precincts.map((p) => `Precinct: ${p}`)], faqs: [],
    schema: [
      webPage(path, title, a.metaDesc),
      ...services.map((s) => ({ "@context": ctx, "@type": "Service", name: `${s.title} ${a.name}`, serviceType: s.title, url: `${SITE}/services/${s.slug}`, provider: { "@id": BIZ_ID }, areaServed: { "@type": "Place", name: `${a.name} ${a.state || "QLD"} ${a.postcode}` } })),
      crumbs([["Service areas", "/#areas"], [a.name, path]]),
    ],
  };
}

export function getPages(): PageSeo[] {
  return [
    {
      path: "/", title: HOME_TITLE, description: HOME_DESC, h1: HOME_H1, intro: ABOUT,
      bullets: SERVICES.map((s) => `${s.title}: ${s.short}`), faqs: HOME_FAQS,
      schema: [businessSchema(), websiteSchema(), webPage("/", HOME_TITLE, HOME_DESC), faqSchema(HOME_FAQS)],
    },
    ...SERVICES.map(servicePage),
    ...AREAS.map(areaPage),
    {
      path: "/projects", title: "Polished Concrete & Epoxy Flooring Projects Gold Coast | All Concept Concrete",
      description: "Polished concrete, epoxy flooring and commercial flooring projects by All Concept Concrete across the Gold Coast — warehouses, showrooms, food production, fitouts and homes.",
      h1: "Floors that work for a living.", intro: ABOUT, bullets: [], faqs: [],
      schema: [webPage("/projects", "Projects", "Project gallery and case studies"), crumbs([["Projects", "/projects"]])],
    },
    {
      path: "/blog", title: "Concrete Flooring Blog | Polished Concrete & Epoxy Advice | All Concept Concrete",
      description: "News, project stories and practical advice on polished concrete, epoxy flooring, commercial flooring and concrete resurfacing from All Concept Concrete, Gold Coast.",
      h1: "News & flooring advice.", intro: ABOUT, bullets: [], faqs: [],
      schema: [webPage("/blog", "Blog", "Concrete flooring news and advice"), crumbs([["Blog", "/blog"]])],
    },
  ];
}

export const getPage = (path: string): PageSeo | undefined => getPages().find((p) => p.path === path);
